import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

/**
 * Níveis de log do fluxo dos componentes NOVOS (rtc-*).
 *
 * O log é o canal do DESENVOLVEDOR (console do navegador); NÃO substitui as
 * mensagens ao usuário (RtcMensagemService/RtcConfirmacaoService) — são canais
 * diferentes. Os componentes legados não usam este service.
 */
export type RtcNivelLog = 'debug' | 'info' | 'aviso' | 'erro';

/**
 * GATE POR PERFIL: os métodos só escrevem quando `environment.profile` está
 * nesta lista. Fora dela, TODOS os níveis (inclusive `erro`) ficam mudos —
 * decisão do dono do projeto. Para habilitar o log no ambiente de
 * desenvolvimento, acrescente 'des' aqui.
 */
const PERFIS_COM_LOG = ['local'];

/**
 * Logger pré-configurado para um escopo (devolvido por `paraEscopo`): os
 * mesmos 4 níveis, sem repetir o prefixo do escopo em cada chamada.
 */
export interface RtcLoggerDeEscopo {
  debug(mensagem: string, ...dados: unknown[]): void;
  info(mensagem: string, ...dados: unknown[]): void;
  aviso(mensagem: string, ...dados: unknown[]): void;
  erro(mensagem: string, ...dados: unknown[]): void;
}

/**
 * Log de FLUXO dos componentes novos: conta no console a história da sessão
 * (recálculos, erros, modais, mensagens), apenas nos perfis habilitados.
 *
 * Saída: `console.debug/info/warn/error` com prefixo `[rtc][<escopo>]` e os
 * `...dados` repassados como argumentos adicionais (o DevTools formata objetos
 * melhor do que string interpolada). Sem estado, sem buffer, sem envio remoto.
 *
 * Uso preferido nos componentes:
 *   private readonly log = inject(RtcLogService).paraEscopo('sn.calculo');
 *   this.log.info('rodada iniciada: 3 item(ns)');
 *
 * Escopos em uso (criar novos no mesmo padrão `area.subarea`):
 *   sn / sn.calculo / sn.item / sn.motor / sn.is — simulador simples nacional;
 *   bcu — simulador base de cálculo unificada; abs — assistente de busca
 *   semântica; modal.<nome> — cada modal; msg — RtcMensagemService;
 *   confirmacao — RtcConfirmacaoService.
 */
@Injectable({ providedIn: 'root' })
export class RtcLogService {

  private readonly habilitado = PERFIS_COM_LOG.includes(environment.profile);

  debug(escopo: string, mensagem: string, ...dados: unknown[]): void {
    this.escrever('debug', escopo, mensagem, dados);
  }

  info(escopo: string, mensagem: string, ...dados: unknown[]): void {
    this.escrever('info', escopo, mensagem, dados);
  }

  aviso(escopo: string, mensagem: string, ...dados: unknown[]): void {
    this.escrever('aviso', escopo, mensagem, dados);
  }

  erro(escopo: string, mensagem: string, ...dados: unknown[]): void {
    this.escrever('erro', escopo, mensagem, dados);
  }

  /** Logger pré-configurado para um escopo (evita repetir o prefixo). */
  paraEscopo(escopo: string): RtcLoggerDeEscopo {
    return {
      debug: (mensagem: string, ...dados: unknown[]) => this.debug(escopo, mensagem, ...dados),
      info: (mensagem: string, ...dados: unknown[]) => this.info(escopo, mensagem, ...dados),
      aviso: (mensagem: string, ...dados: unknown[]) => this.aviso(escopo, mensagem, ...dados),
      erro: (mensagem: string, ...dados: unknown[]) => this.erro(escopo, mensagem, ...dados),
    };
  }

  private escrever(nivel: RtcNivelLog, escopo: string, mensagem: string, dados: unknown[]): void {
    if (!this.habilitado) { return; }
    const texto = `[rtc][${escopo}] ${mensagem}`;
    switch (nivel) {
      case 'debug':
        console.debug(texto, ...dados);
        break;
      case 'info':
        console.info(texto, ...dados);
        break;
      case 'aviso':
        console.warn(texto, ...dados);
        break;
      case 'erro':
        console.error(texto, ...dados);
        break;
    }
  }
}
